// ============================================================================
// Componente: AutomacaoConciliacaoAdyen.js
// Objetivo: Interface interna para conciliação da Adyen no Módulo Financeiro.
// Padrão visual: Financeiro / Verde-azulado
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import {
    Play,
    CheckCircle,
    AlertCircle,
    Loader2,
    FileText,
    UploadCloud,
    Eraser,
    DownloadCloud
} from 'lucide-react';

const AutomacaoConciliacaoAdyen = () => {
    const corModulo = '#16a39c';
    const corModuloEscura = '#0f766e';
    const corFundoClaro = '#f0fdfa';
    const corBorda = '#99f6e4';

    const fileInputRef = useRef(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [arquivos, setArquivos] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [status, setStatus] = useState('idle');
    const [executionId, setExecutionId] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [progress, setProgress] = useState(0);
    const [statusText, setStatusText] = useState('Aguardando inicialização...');

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        let interval;

        if (isProcessing && executionId) {
            interval = setInterval(async () => {
                try {
                    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
                    const response = await fetch(`/api/automacoes-financeiro/logs/${executionId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (response.ok) {
                        const data = await response.json();

                        if (data.progress !== undefined) setProgress(data.progress);

                        if (data.logs && data.logs.length > 0) {
                            const lastLog = data.logs[data.logs.length - 1];
                            const cleanLog = String(lastLog)
                                .replace(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3}\s*-\s*INFO\s*-\s*|\[.*?\]\s*)/g, '')
                                .trim();
                            setStatusText(cleanLog || lastLog);
                        }
                    }
                } catch (error) {
                    console.error('Erro ao buscar progresso:', error);
                }
            }, 1500);
        }

        return () => clearInterval(interval);
    }, [isProcessing, executionId]);

    const resetarTela = () => {
        setArquivos([]);
        setStatus('idle');
        setExecutionId(null);
        setErrorMessage('');
        setProgress(0);
        setStatusText('Aguardando inicialização...');

        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleFileChange = (e) => {
        const arquivosSelecionados = Array.from(e.target.files || []);
        setArquivos(arquivosSelecionados);
        setStatus('idle');
        setErrorMessage('');
        setProgress(0);
        setStatusText('Aguardando inicialização...');
    };

    const iniciarAutomacao = async () => {
        if (arquivos.length === 0) {
            setStatus('error');
            setErrorMessage('Selecione os relatórios da Adyen antes de iniciar.');
            return;
        }

        const newExecutionId = `adyen_${Date.now()}`;
        setExecutionId(newExecutionId);
        setIsProcessing(true);
        setStatus('processing');
        setProgress(5);
        setStatusText('Enviando arquivos para o servidor...');
        setErrorMessage('');

        try {
            const formData = new FormData();
            formData.append('executionId', newExecutionId);
            arquivos.forEach(file => formData.append('arquivos_adyen', file));

            const token = localStorage.getItem('token') || localStorage.getItem('authToken');
            const response = await fetch('/api/automacoes-financeiro/financeiro-conciliacao-adyen', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });

            if (!response.ok) {
                let errorData = {};
                try {
                    errorData = await response.json();
                } catch (_) {}

                throw new Error(errorData.error || 'Ocorreu um erro ao processar os arquivos.');
            }

            setProgress(100);
            setStatusText('Processamento concluído. Baixando relatório...');

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;

            const disposition = response.headers.get('Content-Disposition');
            let fileName = 'relatorio_conciliacao_adyen.xlsx';
            if (disposition && disposition.includes('attachment')) {
                const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
                if (matches?.[1]) fileName = matches[1].replace(/['"]/g, '');
            }

            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            setStatus('success');
            setStatusText('Conciliação finalizada e arquivo baixado com sucesso!');
            setArquivos([]);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (error) {
            console.error('Erro na requisição:', error);
            setStatus('error');
            setErrorMessage(error.message || 'Erro de conexão com o servidor.');
            setStatusText('Falha de comunicação com o servidor.');
        } finally {
            setIsProcessing(false);
        }
    };

    const getProgressBarColor = () => {
        if (status === 'error') return '#ef4444';
        if (status === 'success') return '#10b981';
        return corModulo;
    };

    const cardStyle = {
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        padding: isMobile ? '20px' : '28px',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)'
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={cardStyle}>
                <div style={{
                    backgroundColor: corFundoClaro,
                    borderLeft: `4px solid ${corModulo}`,
                    padding: '16px 20px',
                    borderRadius: '10px',
                    marginBottom: '24px'
                }}>
                    <h4 style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: corModuloEscura,
                        fontSize: '15px',
                        fontWeight: '800',
                        margin: 0
                    }}>
                        <FileText size={18} /> Importação de Relatórios Adyen
                    </h4>
                    <p style={{
                        color: '#115e59',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        margin: '6px 0 0 0'
                    }}>
                        Faça o upload dos relatórios de Detalhes de Liquidação (.xlsx) e Recebimentos (.csv).
                        O sistema fará a conciliação e gerará uma planilha consolidada.
                    </p>
                </div>

                <div style={{
                    backgroundColor: '#f8fafc',
                    border: `2px dashed ${corBorda}`,
                    borderRadius: '14px',
                    padding: '34px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    textAlign: 'center',
                    marginBottom: '24px',
                    position: 'relative'
                }}>
                    <UploadCloud size={42} color={corModulo} />

                    <div>
                        <label style={{ cursor: isProcessing ? 'not-allowed' : 'pointer', color: corModulo, fontWeight: '800', fontSize: '15px' }}>
                            Clique aqui para selecionar os arquivos
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept=".csv,.xlsx"
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                                disabled={isProcessing}
                            />
                        </label>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                            Arquivos suportados: .csv e .xlsx
                        </p>
                    </div>

                    {arquivos.length > 0 && (
                        <div style={{ marginTop: '14px', display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                            {arquivos.map((file, index) => (
                                <span key={`${file.name}-${index}`} style={{
                                    backgroundColor: '#ccfbf1',
                                    color: '#115e59',
                                    fontSize: '12px',
                                    padding: '5px 9px',
                                    borderRadius: '999px',
                                    border: '1px solid #99f6e4',
                                    fontWeight: '700'
                                }}>
                                    {file.name}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {errorMessage && (
                    <div style={{
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#b91c1c',
                        padding: '14px 16px',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '24px',
                        fontSize: '14px',
                        fontWeight: '700'
                    }}>
                        <AlertCircle size={18} /> {errorMessage}
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        onClick={resetarTela}
                        disabled={isProcessing}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 18px',
                            borderRadius: '8px',
                            backgroundColor: '#f1f5f9',
                            color: '#475569',
                            fontWeight: '800',
                            border: '1px solid #cbd5e1',
                            cursor: isProcessing ? 'not-allowed' : 'pointer',
                            width: isMobile ? '100%' : 'auto',
                            justifyContent: 'center'
                        }}
                    >
                        <Eraser size={18} /> Limpar
                    </button>

                    <button
                        type="button"
                        onClick={iniciarAutomacao}
                        disabled={isProcessing || arquivos.length === 0}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 24px',
                            backgroundColor: (isProcessing || arquivos.length === 0) ? '#cbd5e1' : corModulo,
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '800',
                            fontSize: '15px',
                            cursor: (isProcessing || arquivos.length === 0) ? 'not-allowed' : 'pointer',
                            boxShadow: '0 4px 8px -3px rgba(22, 163, 156, 0.45)',
                            width: isMobile ? '100%' : 'auto',
                            justifyContent: 'center'
                        }}
                    >
                        {isProcessing ? (
                            <><Loader2 size={18} className="animate-spin" /> Processando...</>
                        ) : (
                            <><Play size={18} /> Iniciar Conciliação</>
                        )}
                    </button>
                </div>
            </div>

            {status !== 'idle' && (
                <div style={cardStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {status === 'processing' && <Loader2 size={24} color={corModulo} className="animate-spin" />}
                            {status === 'success' && <CheckCircle size={24} color="#10b981" />}
                            {status === 'error' && <AlertCircle size={24} color="#ef4444" />}

                            <div>
                                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>Status da Execução</h4>
                                <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>{statusText}</p>
                            </div>
                        </div>

                        <span style={{ fontSize: '18px', fontWeight: '900', color: getProgressBarColor() }}>
                            {Math.round(progress)}%
                        </span>
                    </div>

                    <div style={{ width: '100%', height: '10px', backgroundColor: '#e2e8f0', borderRadius: '999px', overflow: 'hidden' }}>
                        <div style={{
                            width: `${Math.min(progress, 100)}%`,
                            height: '100%',
                            backgroundColor: getProgressBarColor(),
                            transition: 'width 0.4s ease'
                        }} />
                    </div>

                    {status === 'success' && (
                        <div style={{
                            marginTop: '16px',
                            color: '#047857',
                            backgroundColor: '#ecfdf5',
                            border: '1px solid #a7f3d0',
                            borderRadius: '10px',
                            padding: '12px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            fontWeight: '700'
                        }}>
                            <DownloadCloud size={18} /> Arquivo gerado e download iniciado automaticamente.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AutomacaoConciliacaoAdyen;
